import React, { useState, useEffect, useCallback } from 'react';
import { 
  MdAdd, 
  MdEdit, 
  MdDelete, 
  MdVisibility, 
  MdDownload, 
  MdRefresh, 
  MdCheck, 
  MdClose,
  MdPrint,
  MdFilterList,
  MdWarning,
  MdSearch,
  MdLogout
} from 'react-icons/md';
import styles from './BankDetails.module.scss';
import API_BASE_URL from '../../config/api';
import { useAuth } from '../../context/AuthContext.jsx';
import BankDetailsForm from './BankDetailsForm';
import BankDetailsView from './BankDetailsView';
import BankDetailsEdit from './BankDetailsEdit';

const CentralAccountMU = () => {
  const { makeAuthenticatedRequest, logout } = useAuth();
  const [bankData, setBankData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [referenceCode, setReferenceCode] = useState('');
  
  // Filter states
  const [filters, setFilters] = useState({
    office_name: '',
    account_head_name: '',
    official_email: '',
    bank_name: '',
    account_type: '',
    reference_code: '',
    is_active: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Bulk operations
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Fetch bank details
  const fetchBankDetails = useCallback(async (page = 1, showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      
      const params = new URLSearchParams({
        limit: itemsPerPage,
        offset: (page - 1) * itemsPerPage,
        grouped: 'true' // Use grouped view
      });

      // Add filters
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          params.append(key, filters[key]);
        }
      });

      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/bank-details/?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setBankData(data.results || data);
        setTotalPages(Math.ceil((data.count || data.length) / itemsPerPage));
        setCurrentPage(page);
      } else {
        throw new Error('Failed to fetch bank details');
      }
    } catch (error) {
      setErrorMessage('Error fetching bank details: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage, filters]);

  useEffect(() => {
    fetchBankDetails(1);
  }, [fetchBankDetails]);

  // Handle form success
  const handleFormSuccess = (result) => {
    setSuccessMessage(result.message);
    if (result.referenceCode) {
      setReferenceCode(result.referenceCode);
    }
    setShowForm(false);
    fetchBankDetails(1);
    
    setTimeout(() => {
      setSuccessMessage('');
      setReferenceCode('');
    }, 5000);
  };

  // Handle update
  const handleUpdate = async (result) => {
    setSuccessMessage(result.message);
    setShowEdit(false);
    setSelectedItem(null);
    fetchBankDetails(currentPage);
    
    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this bank detail?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/bank-details/${id}/`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setSuccessMessage('Bank detail deleted successfully!');
        fetchBankDetails(currentPage);
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        throw new Error('Failed to delete bank detail');
      }
    } catch (error) {
      setErrorMessage('Error deleting bank detail: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle delete group
  const handleDeleteGroup = async (group) => {
    if (!window.confirm(`Are you sure you want to delete the entire group for "${group.office_name}"? This will delete all ${group.bank_accounts.length} bank accounts.`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/bank-details/bulk_delete_group/`, {
        method: 'DELETE',
        body: JSON.stringify({
          office_name: group.office_name,
          account_head_name: group.account_head_name,
          official_email: group.official_email
        })
      });

      if (response.ok) {
        setSuccessMessage(`Successfully deleted entire group for "${group.office_name}"!`);
        fetchBankDetails(currentPage);
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete group');
      }
    } catch (error) {
      setErrorMessage('Error deleting group: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle delete all
  const handleDeleteAll = async () => {
    if (!window.confirm('Are you sure you want to delete ALL bank details? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/bank-details/delete-all/`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setSuccessMessage('All bank details deleted successfully!');
        fetchBankDetails(1);
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        throw new Error('Failed to delete all bank details');
      }
    } catch (error) {
      setErrorMessage('Error deleting all bank details: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle activation
  const handleActivation = async (id, action) => {
    const actionText = action === 'activate' ? 'activate' : 'deactivate';
    const confirmMessage = action === 'activate' 
      ? 'Are you sure you want to activate this token for editing?'
      : 'Are you sure you want to deactivate this token?';
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setLoading(true);
      const url = action === 'activate' 
        ? `${API_BASE_URL}/api/bank-details/${id}/activate_editing/`
        : `${API_BASE_URL}/api/bank-details/${id}/deactivate_editing/`;
      
      const data = action === 'activate' 
        ? { duration_hours: 24, admin_username: 'admin' }
        : { admin_username: 'admin' };

      const response = await makeAuthenticatedRequest(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        const result = await response.json();
        const message = action === 'activate' 
          ? `✅ Token activated successfully! ${result.message}`
          : `✅ Token deactivated successfully! ${result.message}`;
        setSuccessMessage(message);
        fetchBankDetails(currentPage);
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        const error = await response.json();
        throw new Error(error.error || `Failed to ${action} bank details`);
      }
    } catch (error) {
      setErrorMessage(`Error ${action}ing bank details: ` + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle Excel download
  const handleDownloadExcel = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      // Add filters to download
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          params.append(key, filters[key]);
        }
      });

      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/bank-details/download-excel/?${params.toString()}`);
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `central_account_bank_details_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setSuccessMessage('Excel file downloaded successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to download Excel file');
      }
    } catch (error) {
      setErrorMessage('Error downloading Excel file: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle bulk operations
  const handleBulkActivation = async (action) => {
    if (selectedItems.length === 0) {
      setErrorMessage('Please select items for bulk operation');
      return;
    }

    try {
      setLoading(true);
      const referenceCodes = selectedItems.map(id => 
        bankData.find(item => item.id === id)?.reference_code
      ).filter(Boolean);

      const url = action === 'activate' 
        ? `${API_BASE_URL}/api/bank-details/bulk_activate/`
        : `${API_BASE_URL}/api/bank-details/bulk_deactivate/`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'POST',
        body: JSON.stringify({
          reference_codes: referenceCodes,
          admin_username: 'admin'
        })
      });

      if (response.ok) {
        const result = await response.json();
        setSuccessMessage(result.message);
        setSelectedItems([]);
        setSelectAll(false);
        fetchBankDetails(currentPage);
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        const error = await response.json();
        throw new Error(error.error || `Failed to ${action} bank details`);
      }
    } catch (error) {
      setErrorMessage(`Error ${action}ing bank details: ` + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle print view
  const handlePrintView = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Central Account MU - Bank Details Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .header h1 { color: #333; margin-bottom: 10px; }
            .header p { color: #666; margin: 0; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
            th { background-color: #f5f5f5; font-weight: bold; }
            .group-header { background-color: #e9ecef; padding: 15px; margin: 20px 0 10px 0; border-radius: 5px; }
            .group-header h3 { margin: 0 0 10px 0; color: #333; }
            .group-info { margin-bottom: 10px; }
            .group-info strong { color: #555; }
            .status-active { color: green; font-weight: bold; }
            .status-inactive { color: red; font-weight: bold; }
            @media print {
              .no-print { display: none; }
              body { margin: 0; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Central Account Management Unit</h1>
            <p>Bank Account Details Report</p>
            <p>Generated on: ${new Date().toLocaleString()}</p>
          </div>
          
          <div class="content">
            ${bankData.map((group, groupIndex) => `
              <div class="group-header">
                <h3>Group ${groupIndex + 1}</h3>
                <div class="group-info">
                  <strong>कार्यालयको नाम:</strong> ${group.office_name || 'N/A'}<br>
                  <strong>लेखा प्रमुखको नाम:</strong> ${group.account_head_name || 'N/A'}<br>
                  <strong>लेखा प्रमुखको official Mail ID:</strong> ${group.official_email || 'N/A'}<br>
                  <strong>Reference Code:</strong> ${group.reference_code}<br>
                  <strong>Status:</strong> <span class="${group.is_editable ? 'status-active' : 'status-inactive'}">${group.is_editable ? 'Active for Editing' : 'Not Editable'}</span>
                </div>
              </div>
              
              <table>
                <thead>
                  <tr>
                    <th>क्र.सं.</th>
                    <th>बैंक नाम</th>
                    <th>खाताको प्रकार</th>
                    <th>खाता नं.</th>
                  </tr>
                </thead>
                <tbody>
                  ${group.bank_accounts.map((account, accountIndex) => `
                    <tr>
                      <td>${accountIndex + 1}</td>
                      <td>${account.bank_name}</td>
                      <td>${account.account_type}</td>
                      <td>${account.account_number}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            `).join('')}
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Handle download grouped Excel
  const handleDownloadGroupedExcel = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      // Add filters to download
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          params.append(key, filters[key]);
        }
      });

      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/bank-details/download-excel/?${params.toString()}`);
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `central_account_bank_details_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setSuccessMessage('Excel file downloaded successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to download Excel file');
      }
    } catch (error) {
      setErrorMessage('Error downloading Excel file: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    fetchBankDetails(1);
  };

  const clearFilters = () => {
    setFilters({
      office_name: '',
      account_head_name: '',
      official_email: '',
      bank_name: '',
      account_type: '',
      reference_code: '',
      is_active: ''
    });
    fetchBankDetails(1);
  };

  // Handle item selection
  const handleItemSelect = (id) => {
    setSelectedItems(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedItems([]);
      setSelectAll(false);
    } else {
      const allIds = bankData.flatMap(group => group.bank_accounts.map(account => account.id));
      setSelectedItems(allIds);
      setSelectAll(true);
    }
  };

  // Handle page change
  const handlePageChange = (page) => {
    fetchBankDetails(page);
  };

  const handleView = (item) => {
    setSelectedItem(item);
    setShowView(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setShowEdit(true);
  };

  return (
    <div className={styles.bankDetailsContainer}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1>Central Account Management Unit</h1>
          <p>Bank Account Details Management System</p>
        </div>
        <div className={styles.headerRight}>
          <button
            onClick={() => window.open('/bank-survey', '_blank')}
            className={styles.formLinkButton}
            title="Open Bank Survey Form in New Tab"
          >
            📋 Bank Survey Form
          </button>
          <button
            onClick={() => setShowForm(true)}
            className={styles.addButton}
            disabled={loading}
          >
            <MdAdd /> Add Bank Details
          </button>
          <button
            onClick={logout}
            className={styles.logoutButton}
            title="Logout"
          >
            <MdLogout /> Logout
          </button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className={styles.successMessage}>
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div className={styles.errorMessage}>
          <MdWarning /> {errorMessage}
        </div>
      )}

      {/* Filters */}
      <div className={styles.filtersSection}>
        <div className={styles.filtersHeader}>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={styles.filterToggleButton}
          >
            <MdFilterList /> Filters
          </button>
          {showFilters && (
            <div className={styles.filterButtons}>
              <button onClick={applyFilters} className={styles.applyButton}>
                <MdSearch /> Apply
              </button>
              <button onClick={clearFilters} className={styles.clearButton}>
                Clear
              </button>
            </div>
          )}
        </div>
        
        {showFilters && (
          <div className={styles.filtersContainer}>
            <div className={styles.filterGroup}>
              <label>Office Name</label>
              <input
                type="text"
                value={filters.office_name}
                onChange={(e) => handleFilterChange('office_name', e.target.value)}
                placeholder="Filter by office name"
              />
            </div>
            <div className={styles.filterGroup}>
              <label>Account Head Name</label>
              <input
                type="text"
                value={filters.account_head_name}
                onChange={(e) => handleFilterChange('account_head_name', e.target.value)}
                placeholder="Filter by account head name"
              />
            </div>
            <div className={styles.filterGroup}>
              <label>Official Email</label>
              <input
                type="text"
                value={filters.official_email}
                onChange={(e) => handleFilterChange('official_email', e.target.value)}
                placeholder="Filter by official email"
              />
            </div>
            <div className={styles.filterGroup}>
              <label>Bank Name</label>
              <input
                type="text"
                value={filters.bank_name}
                onChange={(e) => handleFilterChange('bank_name', e.target.value)}
                placeholder="Filter by bank name"
              />
            </div>
            <div className={styles.filterGroup}>
              <label>Account Type</label>
              <input
                type="text"
                value={filters.account_type}
                onChange={(e) => handleFilterChange('account_type', e.target.value)}
                placeholder="Filter by account type"
              />
            </div>
            <div className={styles.filterGroup}>
              <label>Reference Code</label>
              <input
                type="text"
                value={filters.reference_code}
                onChange={(e) => handleFilterChange('reference_code', e.target.value)}
                placeholder="Filter by reference code"
              />
            </div>
            <div className={styles.filterGroup}>
              <label>Editing Status</label>
              <select
                value={filters.is_active}
                onChange={(e) => handleFilterChange('is_active', e.target.value)}
              >
                <option value="">All</option>
                <option value="true">Active for Editing</option>
                <option value="false">Not Editable</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Actions */}
      {selectedItems.length > 0 && (
        <div className={styles.bulkActions}>
          <span>{selectedItems.length} item(s) selected</span>
          <div className={styles.bulkButtons}>
            <button 
              onClick={() => handleBulkActivation('activate')}
              disabled={loading}
              className={styles.activateButton}
            >
              <MdCheck /> Activate for Editing
            </button>
            <button 
              onClick={() => handleBulkActivation('deactivate')}
              disabled={loading}
              className={styles.deactivateButton}
            >
              <MdClose /> Deactivate Editing
            </button>
          </div>
        </div>
      )}

      {/* Actions Bar */}
      <div className={styles.actionsBar}>
        <div className={styles.actionsLeft}>
          <button 
            onClick={() => fetchBankDetails(currentPage)}
            disabled={loading}
            className={styles.refreshButton}
          >
            <MdRefresh /> Refresh
          </button>
          <button 
            onClick={handlePrintView}
            disabled={loading}
            className={styles.printButton}
          >
            <MdPrint /> Print View
          </button>
          <button 
            onClick={handleDownloadGroupedExcel}
            disabled={loading}
            className={styles.downloadButton}
          >
            <MdDownload /> Download Excel
          </button>
          <button 
            onClick={handleDeleteAll}
            disabled={loading}
            className={styles.deleteAllButton}
            title="Delete All Bank Details"
          >
            🗑️ Delete All
          </button>
        </div>
        
        <div className={styles.actionsRight}>
          <span className={styles.itemCount}>
            {bankData.length} of {totalPages * itemsPerPage} items
          </span>
        </div>
      </div>

      {/* Data Table */}
      <div className={styles.tableContainer}>
        {loading ? (
          <div className={styles.loading}>
            <div className={styles.spinner}></div>
            <p>Loading bank details...</p>
          </div>
        ) : (
          <div className={styles.groupedDataContainer}>
            {bankData.map((group, groupIndex) => (
              <div key={groupIndex} className={styles.groupedItem}>
                {/* Office Information Header */}
                <div className={styles.officeHeader}>
                  <div className={styles.officeInfo}>
                    <div className={styles.officeDetails}>
                      <div className={styles.officeField}>
                        <strong>कार्यालयको नाम.-</strong> {group.office_name || 'N/A'}
                      </div>
                      <div className={styles.officeField}>
                        <strong>लेखा प्रमुखको नाम.-</strong> {group.account_head_name || 'N/A'}
                      </div>
                      <div className={styles.officeField}>
                        <strong>लेखा प्रमुखको official Mail ID.-</strong> {group.official_email || 'N/A'}
                      </div>
                      <div className={styles.officeField}>
                        <strong>टोकन स्थिति:</strong> 
                        <span className={group.is_editable ? styles.activeStatus : styles.inactiveStatus}>
                          {group.is_editable ? 'सक्रिय (सम्पादन गर्न सकिन्छ)' : 'निष्क्रिय (सम्पादन गर्न मिल्दैन)'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className={styles.groupActions}>
                    <button
                      onClick={() => handleView(group)}
                      className={styles.viewButton}
                      title="View Details"
                    >
                      <MdVisibility />
                    </button>
                    <button
                      onClick={() => handleEdit(group)}
                      className={styles.editButton}
                      disabled={!group.is_editable}
                      title={group.is_editable ? "Edit Details" : "Not Editable"}
                    >
                      <MdEdit />
                    </button>
                    <button
                      onClick={() => handleDeleteGroup(group)}
                      className={styles.deleteButton}
                      title="Delete Entire Group"
                    >
                      <MdDelete />
                    </button>
                  </div>
                </div>

                {/* Bank Accounts Table */}
                <div className={styles.bankAccountsSection}>
                  <h4>कार्यालयको नाममा सञ्चालित सम्पूर्ण बैक खाताको विवरण</h4>
                  <table className={styles.bankAccountsTable}>
                    <thead>
                      <tr>
                        <th>क्र.सं.</th>
                        <th>बैंक नाम</th>
                        <th>खाताको प्रकार</th>
                        <th>खाता नं.</th>
                        <th>Reference Code</th>
                        <th>Status</th>
                        <th title="Click the buttons to activate/deactivate tokens for editing">टोकन स्थिति / नियन्त्रण ℹ️</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.bank_accounts.map((account, accountIndex) => (
                        <tr key={account.id} className={account.is_editable ? styles.editable : ''}>
                          <td>{accountIndex + 1}</td>
                          <td>{account.bank_name}</td>
                          <td>{account.account_type}</td>
                          <td>{account.account_number}</td>
                          <td className={styles.referenceCode}>
                            <strong>{group.reference_code}</strong>
                          </td>
                          <td>
                            <span className={account.is_editable ? styles.editableBadge : styles.notEditableBadge}>
                              {account.is_editable ? 'Editable' : 'Not Editable'}
                            </span>
                          </td>
                          <td>
                            <div className={styles.tokenStatusContainer}>
                              <span className={account.is_editable ? styles.activeStatus : styles.inactiveStatus}>
                                {account.is_editable ? 'सक्रिय (सम्पादन गर्न सकिन्छ)' : 'निष्क्रिय (सम्पादन गर्न मिल्दैन)'}
                              </span>
                              <div className={styles.tokenActions}>
                                {account.is_editable ? (
                                  <button
                                    onClick={() => handleActivation(account.id, 'deactivate')}
                                    className={styles.deactivateTokenButton}
                                    title="Deactivate Token"
                                    disabled={loading}
                                  >
                                    <MdClose />
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleActivation(account.id, 'activate')}
                                    className={styles.activateTokenButton}
                                    title="Activate Token"
                                    disabled={loading}
                                  >
                                    <MdCheck />
                                  </button>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={styles.pageButton}
          >
            Previous
          </button>
          <span className={styles.pageInfo}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={styles.pageButton}
          >
            Next
          </button>
        </div>
      )}

      {/* Modals */}
      {showForm && (
        <BankDetailsForm
          onSuccess={handleFormSuccess}
          onCancel={() => setShowForm(false)}
        />
      )}

      {showView && selectedItem && (
        <BankDetailsView
          item={selectedItem}
          onClose={() => {
            setShowView(false);
            setSelectedItem(null);
          }}
        />
      )}

      {showEdit && selectedItem && (
        <BankDetailsEdit
          item={selectedItem}
          onSubmit={handleUpdate}
          onClose={() => {
            setShowEdit(false);
            setSelectedItem(null);
          }}
        />
      )}
    </div>
  );
};

export default CentralAccountMU;
